import React, { useState } from 'react';
import { useAppContext } from '../../context/appContext/appContext';

import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

import { userProfileSchema, userSchema } from '../../utils/joiSchema';
import { dict } from '../../translate/translate-dictionary';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

import { UserProfileDB, UserProfileFormInputs } from '../users/types';

type Props = {
	editData: UserProfileDB | null;
	onClose: () => void;
	onFormSubmit: (data: UserProfileFormInputs) => Promise<string | undefined>;
};

const UserProfileForm = ({ editData, onClose, onFormSubmit }: Props) => {
	const [masterError, setMasterError] = useState<string>('');
	const { state } = useAppContext();

	const lang = state.lang;

	const {
		register,
		handleSubmit,

		formState: { errors },
	} = useForm<UserProfileFormInputs>({
		defaultValues: {
			firstName: editData ? editData.firstName : '',
			lastName: editData ? editData.lastName : '',
			email: editData ? editData.email : '',
			phone: editData ? editData.phone : '',
			street: editData ? editData.street : '',
			city: editData ? editData.city : '',
			zipCode: editData ? editData.zipCode : '',
		},
		resolver: joiResolver(userProfileSchema),
		mode: 'onChange',
		delayError: 2000,
	});

	const onSubmit = async (data: UserProfileFormInputs) => {
		let newZipCode = '';
		if (data.zipCode && data.zipCode.length === 5)
			newZipCode = `${data.zipCode.substring(0, 3)} ${data.zipCode.substring(3, data.zipCode.length)}`;

		console.log(newZipCode);

		const newData = { ...data, zipCode: newZipCode ? newZipCode : data.zipCode };

		const response = await onFormSubmit(newData);

		if (response) setMasterError(response);
	};

	return (
		<>
			<Box
				component="form"
				noValidate
				onSubmit={handleSubmit(onSubmit)}
				sx={{
					mt: 1,
				}}>
				{/* ________ FIRST and LAST NAME _______ */}

				<Box
					sx={{
						display: 'flex',
						justifyContent: { sm: 'space-between' },
						flexDirection: { xs: 'column', sm: 'row' },
						alignItems: 'center',
					}}>
					<TextField
						{...register('firstName')}
						required
						margin="normal"
						label="Jméno"
						autoFocus
						error={!!errors?.firstName}
						helperText={
							errors?.firstName?.message
								? dict[errors.firstName.message]
									? dict[errors.firstName.message][lang]
									: errors?.firstName?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 100 }}
						sx={{ width: { xs: '100%', sm: '48%' } }}
					/>

					<TextField
						{...register('lastName')}
						required
						margin="normal"
						label="Příjmení"
						error={!!errors?.lastName}
						helperText={
							errors?.lastName?.message
								? dict[errors.lastName.message]
									? dict[errors.lastName.message][lang]
									: errors?.lastName?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 100 }}
						sx={{ width: { xs: '100%', sm: '48%' } }}
					/>
				</Box>

				{/* ________ EMAIL and PHONE _______ */}

				<Box
					sx={{
						display: 'flex',
						justifyContent: { sm: 'space-between' },
						flexDirection: { xs: 'column', sm: 'row' },
						alignItems: 'center',
					}}>
					<TextField
						{...register('email')}
						required
						margin="normal"
						label="Email"
						error={!!errors?.email}
						helperText={
							errors?.email?.message
								? dict[errors.email.message]
									? dict[errors.email.message][lang]
									: errors?.email?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 100 }}
						sx={{ width: { xs: '100%', sm: '48%' } }}
					/>
					<TextField
						{...register('phone')}
						margin="normal"
						label="Telefon"
						error={!!errors?.phone}
						helperText={
							errors?.phone?.message
								? dict[errors.phone.message]
									? dict[errors.phone.message][lang]
									: errors?.phone?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 20 }}
						sx={{ width: { xs: '100%', sm: '48%' } }}
					/>
				</Box>

				{/* ________ STREET _______ */}

				<Box
					sx={{
						display: 'flex',
						justifyContent: { sm: 'space-between' },
						flexDirection: { xs: 'column', sm: 'column' },
						alignItems: 'center',
					}}>
					<TextField
						{...register('street')}
						margin="normal"
						label="Ulice a číslo popisné"
						error={!!errors?.street}
						helperText={
							errors?.street?.message
								? dict[errors.street.message]
									? dict[errors.street.message][lang]
									: errors?.street?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 100 }}
						sx={{ width: { xs: '100%', sm: '100%' } }}
					/>
				</Box>

				{/* ________ ZIP CODE and CITY _______ */}

				<Box
					sx={{
						display: 'flex',
						justifyContent: { xs: 'space-between' },
						flexDirection: { xs: 'column', sm: 'row' },
						alignItems: 'center',
					}}>
					<TextField
						{...register('zipCode')}
						margin="normal"
						label="PSČ"
						error={!!errors?.zipCode}
						helperText={
							errors?.zipCode?.message
								? dict[errors.zipCode.message]
									? dict[errors.zipCode.message][lang]
									: errors?.zipCode?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 6 }}
						sx={{ width: { xs: '100%', sm: '38%' } }}
					/>

					<TextField
						{...register('city')}
						margin="normal"
						label="Město"
						error={!!errors?.city}
						helperText={
							errors?.city?.message
								? dict[errors.city.message]
									? dict[errors.city.message][lang]
									: errors?.city?.message
								: ' '
						}
						variant="filled"
						size="medium"
						inputProps={{ maxLength: 100 }}
						sx={{ width: { xs: '100%', sm: '58%' } }}
					/>
				</Box>

				<Stack sx={{ width: '100%', marginTop: 2 }} spacing={3}>
					{masterError && (
						<Alert sx={{ borderRadius: '0.5em' }} severity="error">
							{dict[masterError] ? dict[masterError][lang] : masterError ? masterError : ' '}
						</Alert>
					)}
				</Stack>

				<Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
					<Button variant="contained" size="large" color="inherit" onClick={onClose}>
						zrušit
					</Button>
					<Button variant="contained" type="submit" size="large">
						Editovat
						<Box component={'span'} sx={{ display: { xs: 'none', sm: 'inline' } }}>
							&nbsp;profil
						</Box>
					</Button>
				</Stack>
			</Box>
		</>
	);
};

export default UserProfileForm;
